// Stable-sentence emission over Transcribe partials (TT-1103 lever 1).
//
// Transcribe finalizes a result at a pause or a speaker change, so on
// continuous speech a FINAL can arrive carrying 30 s of words — its first
// sentence is 30 s old before the spine sees it. With partial-results
// stabilization on (appback presigns it), every item in a partial carries
// `Stable`; a stable item will not change. This cursor drips each sentence
// as soon as its terminator is stable, drips a stable run-on at a clause
// boundary once it has spanned STABLE_MAX_SPAN_S, and hands the FINAL only
// what was not already dripped — so the words → analysis clock starts at the
// sentence, not the segment. Mirrored in domain/livetap/host_client/stable_drips.py.
//
// Speaker labels arrive only with the FINAL (measured 2026-09-16: no partial
// item carries `Speaker`), so an early sentence cannot be attributed when it
// goes out. Each early sentence goes out under a *pending block* of its own,
// named after the drip that opened it (`pendingSpeaker`); the FINAL then
// names every block — `speakers: {drip_num: name, …}` on its drip — each
// from the labels its own words carry in the FINAL, and drips the rest split
// at the labels. The spine fills the names in. A block per sentence, not per
// result: one result can hold several speakers (30 s, three of them, is
// common), and naming the whole result after its majority speaker doubled
// the lane's DER against Transcribe's own per-word labels (TT-1093, run b2).
//
// Per result (`ResultId`) the cursor remembers how many leading items went
// out and, for each early sentence, its item span and the drip that opened it:
//   partial — the undripped stable prefix up to its last sentence terminator,
//             or — with no terminator and STABLE_MAX_SPAN_S of speech in it —
//             up to its last clause boundary (its end when it has none); it
//             goes out a sentence at a time.
//   FINAL   — everything after the dripped prefix, plus each early
//             sentence's block and the majority label over that sentence's
//             words in the FINAL. Then forgotten.
// Stabilization off (no `Stable` flags) degrades to FINAL-only (TT-1102).

const SENTENCE_TERMINATORS = ".?!";
const CLAUSE_BOUNDARIES = ",;:";
// A stable run-on this long (media seconds) goes out at its last clause.
export const STABLE_MAX_SPAN_S = 4.0;

// The spine's pending-block name (domain/livetap/contract.py pending_speaker).
export function pendingSpeaker(dripNum) {
  return `Speaker (${dripNum})`;
}

function isPunct(item, marks) {
  if (item?.Type !== "punctuation") return false;
  const content = (item.Content || "").replace(/["')\]]+$/, "");
  return content.length > 0 && marks.includes(content[content.length - 1]);
}

export function isTerminator(item) {
  return isPunct(item, SENTENCE_TERMINATORS);
}

export function isClauseBoundary(item) {
  return isPunct(item, CLAUSE_BOUNDARIES);
}

const isWord = (item) => item.Type !== "punctuation";

function resultItems(result) {
  return result?.Alternatives?.[0]?.Items || [];
}

// End (exclusive) of the drippable slice from `start`: the stable prefix cut
// at its last sentence terminator; failing that, at its last clause boundary
// (or its end) once it spans `maxSpanS` of speech; `start` when neither.
export function stableCut(items, start, maxSpanS = STABLE_MAX_SPAN_S) {
  let end = start;
  while (end < items.length && items[end].Stable) end += 1;
  if (end <= start) return start;
  for (let i = end - 1; i >= start; i -= 1) {
    if (isTerminator(items[i])) return i + 1;
  }
  const words = items.slice(start, end).filter(isWord);
  if (!words.length) return start;
  const t0 = words[0].StartTime;
  const t1 = words[words.length - 1].EndTime;
  if (t0 == null || t1 == null || t1 - t0 < maxSpanS) return start;
  for (let i = end - 1; i >= start; i -= 1) {
    if (isClauseBoundary(items[i])) return i + 1;
  }
  return end;
}

// The label most of the words carry; null when they carry none.
export function majorityLabel(items) {
  const counts = new Map();
  for (const it of items) {
    if (!isWord(it) || it.Speaker == null) continue;
    counts.set(it.Speaker, (counts.get(it.Speaker) || 0) + 1);
  }
  let best = null;
  let bestCount = 0;
  for (const [label, n] of counts) {
    if (n > bestCount) {
      best = label;
      bestCount = n;
    }
  }
  return best;
}

// `items[start:end]` cut after each sentence terminator, as [start, end]
// spans. A span with no word (stray punctuation) joins the one before it.
export function sentenceSpans(items, start, end) {
  const spans = [];
  let s = start;
  for (let i = start; i < end; i += 1) {
    if (isTerminator(items[i])) {
      spans.push([s, i + 1]);
      s = i + 1;
    }
  }
  if (s < end) spans.push([s, end]);
  const out = [];
  for (const [a, b] of spans) {
    if (out.length && !items.slice(a, b).some(isWord)) out[out.length - 1][1] = b;
    else out.push([a, b]);
  }
  return out;
}

// A take: { resultId, items, pending, blocks }
//   pending — early (unlabeled) sentences, each an item list: each goes out
//             under a pending block of its own, and the caller reports each
//             block's drip number (`opened`)
//   blocks  — the FINAL of a result that had early sentences: [dripNum,
//             label] per block in order, the label what that sentence's
//             words carry in the FINAL; `items` is the rest, which continues
//             the last block
export function createResultCursor(maxSpanS = STABLE_MAX_SPAN_S) {
  const dripped = new Map();
  // resultId -> [{ num (null until opened), start, end }] per early sentence
  const blocks = new Map();
  const take = (resultId, items = [], extra = {}) => ({
    resultId,
    items,
    pending: [],
    blocks: [],
    ...extra,
  });
  return {
    // A new Transcribe session: fresh result ids, nothing in flight.
    reset() {
      dripped.clear();
      blocks.clear();
    },
    // The caller numbered the drip that opened `resultId`'s next early
    // sentence (in the order `take` gave them).
    opened(resultId, dripNum) {
      const block = (blocks.get(resultId) || []).find((b) => b.num == null);
      if (block) block.num = dripNum;
    },
    // What to drip for `result` now (possibly nothing).
    take(result) {
      const items = resultItems(result);
      const id = result?.ResultId;
      if (result?.IsPartial) {
        if (id == null) return take(null);
        const start = dripped.get(id) || 0;
        const cut = stableCut(items, start, maxSpanS);
        if (cut <= start) return take(id);
        dripped.set(id, cut);
        const spans = sentenceSpans(items, start, cut);
        if (!blocks.has(id)) blocks.set(id, []);
        blocks.get(id).push(...spans.map(([a, b]) => ({ num: null, start: a, end: b })));
        return take(id, [], { pending: spans.map(([a, b]) => items.slice(a, b)) });
      }
      const start = id == null ? 0 : dripped.get(id) || 0;
      const opened = id == null ? [] : blocks.get(id) || [];
      if (id != null) {
        dripped.delete(id);
        blocks.delete(id);
      }
      return take(id, items.slice(start), {
        blocks: opened
          .filter((b) => b.num != null)
          .map((b) => [b.num, majorityLabel(items.slice(b.start, b.end))]),
      });
    },
  };
}
